import { useLocalStorageState } from "@istari-frontend/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import type { KeyboardEvent, PointerEvent } from "react";

export interface AiChatWindowSize {
  width: number;
  height: number;
}

export const AI_CHAT_DOCK_TRANSITION_MS = 150;

export const AI_CHAT_WINDOW_DEFAULT_SIZE: AiChatWindowSize = { width: 480, height: 600 };
export const AI_CHAT_WINDOW_MIN_SIZE: AiChatWindowSize = { width: 320, height: 400 };

/** Main app column width preserved when chat is docked on the right (chat cannot resize into this). */
export const AI_CHAT_DOCKED_MAIN_MIN_WIDTH_PX = 600;

const STORAGE_KEY = "istari-ai-chat-window-size";
const VIEWPORT_MARGIN = 24;
const KEYBOARD_STEP = 16;

function setAiChatResizeLock(locked: boolean) {
  if (typeof document === "undefined") return;
  if (locked) {
    document.documentElement.dataset.aiChatResizing = "";
  } else {
    delete document.documentElement.dataset.aiChatResizing;
  }
}

export type AiChatDockWidthConstraint = "right" | null;

export function maxAiChatWidthWhenDockedRight(viewportWidth = window.innerWidth): number {
  const sidebarGap = document.querySelector('[data-slot="sidebar-gap"]');
  const sidebarWidth = sidebarGap?.getBoundingClientRect().width ?? 0;
  return Math.max(AI_CHAT_WINDOW_MIN_SIZE.width, viewportWidth - sidebarWidth - AI_CHAT_DOCKED_MAIN_MIN_WIDTH_PX);
}

interface AiChatLayoutCaps {
  maxWidth: number;
  maxHeight: number;
}

export function measureAiChatLayoutCaps(
  dockWidthConstraint: AiChatDockWidthConstraint = null,
  viewport: AiChatWindowSize = viewportSize(),
): AiChatLayoutCaps {
  let maxWidth = Math.max(AI_CHAT_WINDOW_MIN_SIZE.width, viewport.width - VIEWPORT_MARGIN * 2);
  if (dockWidthConstraint === "right") {
    maxWidth = Math.min(maxWidth, maxAiChatWidthWhenDockedRight(viewport.width));
  }
  const maxHeight = Math.max(AI_CHAT_WINDOW_MIN_SIZE.height, viewport.height - VIEWPORT_MARGIN * 2);
  return { maxWidth, maxHeight };
}

function clampToLayoutCaps(size: AiChatWindowSize, caps: AiChatLayoutCaps): AiChatWindowSize {
  return {
    width: Math.round(Math.min(Math.max(size.width, AI_CHAT_WINDOW_MIN_SIZE.width), caps.maxWidth)),
    height: Math.round(Math.min(Math.max(size.height, AI_CHAT_WINDOW_MIN_SIZE.height), caps.maxHeight)),
  };
}

export function clampAiChatWindowSize(
  size: AiChatWindowSize,
  viewport: AiChatWindowSize,
  dockWidthConstraint: AiChatDockWidthConstraint = null,
): AiChatWindowSize {
  return clampToLayoutCaps(size, measureAiChatLayoutCaps(dockWidthConstraint, viewport));
}

interface DragOrigin {
  pointerX: number;
  pointerY: number;
  size: AiChatWindowSize;
  /** Snapshot at drag start so pointermove does not re-query sidebar layout. */
  layoutCaps: AiChatLayoutCaps;
}

export interface AiChatResizeHandleProps {
  onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: PointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: PointerEvent<HTMLElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  onDoubleClick: () => void;
}

export function useAiChatWindowSize(dockWidthConstraint: AiChatDockWidthConstraint = null): {
  size: AiChatWindowSize;
  isResizing: boolean;
  resizeHandleProps: AiChatResizeHandleProps;
} {
  const [storedSize, setStoredSize] = useLocalStorageState<AiChatWindowSize>(STORAGE_KEY, AI_CHAT_WINDOW_DEFAULT_SIZE);
  const [dragSize, setDragSize] = useState<AiChatWindowSize | null>(null);
  const [layoutCaps, setLayoutCaps] = useState(() => measureAiChatLayoutCaps(dockWidthConstraint));
  const dragOrigin = useRef<DragOrigin | null>(null);

  const refreshLayoutCaps = useCallback(() => {
    setLayoutCaps(measureAiChatLayoutCaps(dockWidthConstraint));
  }, [dockWidthConstraint]);

  useEffect(() => {
    refreshLayoutCaps();
    window.addEventListener("resize", refreshLayoutCaps);
    return () => window.removeEventListener("resize", refreshLayoutCaps);
  }, [refreshLayoutCaps]);

  useEffect(() => {
    if (dockWidthConstraint !== "right") return undefined;
    const sidebarGap = document.querySelector('[data-slot="sidebar-gap"]');
    if (!(sidebarGap instanceof Element)) return undefined;
    const observer = new ResizeObserver(refreshLayoutCaps);
    observer.observe(sidebarGap);
    return () => observer.disconnect();
  }, [dockWidthConstraint, refreshLayoutCaps]);

  const onPointerDown = (event: PointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    // preventDefault suppresses click-to-focus, so focus explicitly to keep keyboard resize reachable.
    event.currentTarget.focus();
    setAiChatResizeLock(true);
    const caps = measureAiChatLayoutCaps(dockWidthConstraint);
    dragOrigin.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      size: clampToLayoutCaps(storedSize, caps),
      layoutCaps: caps,
    };
  };

  const onPointerMove = (event: PointerEvent<HTMLElement>) => {
    if (!dragOrigin.current) return;
    setDragSize(sizeFromDrag(dragOrigin.current, event));
  };

  const onPointerUp = (event: PointerEvent<HTMLElement>) => {
    const origin = dragOrigin.current;
    if (!origin) return;
    dragOrigin.current = null;
    setDragSize(null);
    setStoredSize(sizeFromDrag(origin, event));
    setAiChatResizeLock(false);
  };

  const onPointerCancel = () => {
    dragOrigin.current = null;
    setDragSize(null);
    setAiChatResizeLock(false);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const delta = keyboardDelta(event.key);
    if (!delta) return;
    event.preventDefault();
    setStoredSize(
      clampToLayoutCaps(
        { width: storedSize.width + delta.width, height: storedSize.height + delta.height },
        layoutCaps,
      ),
    );
  };

  const onDoubleClick = () => setStoredSize(clampToLayoutCaps(AI_CHAT_WINDOW_DEFAULT_SIZE, layoutCaps));

  useEffect(() => {
    return () => {
      setAiChatResizeLock(false);
    };
  }, []);

  return {
    size: clampToLayoutCaps(dragSize ?? storedSize, layoutCaps),
    isResizing: dragSize !== null,
    resizeHandleProps: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onKeyDown, onDoubleClick },
  };
}

// The window is anchored bottom-right, so dragging the top-left handle up or left grows it.
function sizeFromDrag(origin: DragOrigin, event: PointerEvent<HTMLElement>): AiChatWindowSize {
  return clampToLayoutCaps(
    {
      width: origin.size.width + (origin.pointerX - event.clientX),
      height: origin.size.height + (origin.pointerY - event.clientY),
    },
    origin.layoutCaps,
  );
}

function keyboardDelta(key: string): AiChatWindowSize | null {
  switch (key) {
    case "ArrowLeft":
      return { width: KEYBOARD_STEP, height: 0 };
    case "ArrowRight":
      return { width: -KEYBOARD_STEP, height: 0 };
    case "ArrowUp":
      return { width: 0, height: KEYBOARD_STEP };
    case "ArrowDown":
      return { width: 0, height: -KEYBOARD_STEP };
    default:
      return null;
  }
}

function viewportSize(): AiChatWindowSize {
  return { width: window.innerWidth, height: window.innerHeight };
}
